// src/pages/AnalyticsDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArticleIcon from '@mui/icons-material/Article';
import ChurchIcon from '@mui/icons-material/Church';
import CommentIcon from '@mui/icons-material/Comment';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import {
  getAdminOverview,
  getEngagementAnalytics,
  getChurchAnalytics,
  getAlarmStatistics,
} from '../services/adminService';
import {
  AdminOverviewResponse,
  EngagementAnalyticsResponse,
  ChurchAnalyticsResponse,
  AlarmStatisticsResponse,
} from '../types/admin';
import UserAnalyticsTab from '../components/admin/UserAnalyticsTab';
import { useApiError } from '../hooks/useApiError';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

// Color palette for charts
const COLORS = ['#2563eb', '#7c3aed', '#16a34a', '#d97706', '#dc2626', '#0891b2', '#4f46e5', '#059669'];

const AnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useApiError();
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [engagement, setEngagement] = useState<EngagementAnalyticsResponse | null>(null);
  const [churches, setChurches] = useState<ChurchAnalyticsResponse | null>(null);
  const [alarms, setAlarms] = useState<AlarmStatisticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [overviewRes, engagementRes, churchesRes, alarmsRes] = await Promise.all([
        getAdminOverview(),
        getEngagementAnalytics(period),
        getChurchAnalytics(),
        getAlarmStatistics(period),
      ]);
      setOverview(overviewRes);
      setEngagement(engagementRes);
      setChurches(churchesRes);
      setAlarms(alarmsRes);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || t('common.error'));
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [period, t, handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error && !overview) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#1e293b">
            {t('analytics.title', 'Analytics Dashboard')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('analytics.subtitle', 'Vue d\'ensemble des performances de la plateforme')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid #e2e8f0',
                '&.Mui-selected': { bgcolor: '#2563eb', color: 'white', '&:hover': { bgcolor: '#1d4ed8' } },
              },
            }}
          >
            <ToggleButton value={7}>7j</ToggleButton>
            <ToggleButton value={30}>30j</ToggleButton>
            <ToggleButton value={90}>90j</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title={t('common.refresh', 'Actualiser')}>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab icon={<VisibilityIcon />} iconPosition="start" label={t('analytics.tabs.overview', 'Vue d\'ensemble')} />
          <Tab icon={<PeopleIcon />} iconPosition="start" label={t('analytics.tabs.users', 'Utilisateurs')} />
          <Tab icon={<ArticleIcon />} iconPosition="start" label={t('analytics.tabs.engagement', 'Engagement')} />
          <Tab icon={<ChurchIcon />} iconPosition="start" label={t('analytics.tabs.churches', 'Églises')} />
          <Tab icon={<NotificationsActiveIcon />} iconPosition="start" label={t('analytics.tabs.alarms', 'Alarmes')} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <OverviewTab overview={overview} engagement={engagement} churches={churches} loading={loading} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <UserAnalyticsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <EngagementTab engagement={engagement} loading={loading} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ChurchesTab churches={churches} loading={loading} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <AlarmsTab alarms={alarms} loading={loading} />
      </TabPanel>
    </Box>
  );
};

// ===== OVERVIEW TAB =====
interface OverviewTabProps {
  overview: AdminOverviewResponse | null;
  engagement: EngagementAnalyticsResponse | null;
  churches: ChurchAnalyticsResponse | null;
  loading: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ overview, engagement, churches, loading }) => {
  const { t } = useTranslation();

  if (!overview) return null;

  const statsCards = [
    {
      title: t('analytics.totalUsers', 'Utilisateurs'),
      value: overview.users.total,
      change: overview.users.growth_rate_7d,
      icon: <PeopleIcon />,
      color: '#2563eb',
      bgColor: '#dbeafe',
    },
    {
      title: t('analytics.totalChurches', 'Églises'),
      value: overview.churches.total,
      subtitle: `${overview.churches.active} ${t('analytics.active', 'actives')}`,
      icon: <ChurchIcon />,
      color: '#7c3aed',
      bgColor: '#f3e8ff',
    },
    {
      title: t('analytics.totalPosts', 'Posts'),
      value: overview.content.posts.total,
      subtitle: `+${overview.content.posts.today} ${t('analytics.today', 'aujourd\'hui')}`,
      icon: <ArticleIcon />,
      color: '#16a34a',
      bgColor: '#dcfce7',
    },
    {
      title: t('analytics.totalEvents', 'Événements'),
      value: overview.events.total,
      subtitle: `${overview.events.upcoming} ${t('analytics.upcoming', 'à venir')}`,
      icon: <CalendarTodayIcon />,
      color: '#d97706',
      bgColor: '#fef3c7',
    },
  ];

  // Prepare pie chart data for user roles
  const roleData = [
    { name: 'Users', value: overview.users.by_role.user, color: '#2563eb' },
    { name: 'Admins', value: overview.users.by_role.admin, color: '#7c3aed' },
    { name: 'Super Admins', value: overview.users.by_role.super_admin, color: '#d97706' },
  ];

  // Content activity data
  const contentData = [
    { name: 'Posts', value: overview.content.posts.total, today: overview.content.posts.today },
    { name: 'Comments', value: overview.content.comments.total, today: overview.content.comments.today },
    { name: 'Reactions', value: overview.content.reactions.total, today: overview.content.reactions.today },
  ];

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={6} md={3} key={index}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                      {stat.value.toLocaleString()}
                    </Typography>
                    {stat.change && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                        {parseFloat(stat.change) >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                        )}
                        <Typography variant="caption" color={parseFloat(stat.change) >= 0 ? '#16a34a' : '#dc2626'} fontWeight={600}>
                          {stat.change}
                        </Typography>
                      </Stack>
                    )}
                    {stat.subtitle && (
                      <Typography variant="caption" color="text.secondary">
                        {stat.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: stat.bgColor, borderRadius: 2 }}>
                    {React.cloneElement(stat.icon, { sx: { color: stat.color, fontSize: 24 } })}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* User Role Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.userDistribution', 'Répartition des utilisateurs')}
              </Typography>
              <Box sx={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={roleData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {roleData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                      formatter={(value: number) => [value.toLocaleString(), '']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Content Activity */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.contentActivity', 'Activité du contenu')}
              </Typography>
              <Stack spacing={3} sx={{ mt: 2 }}>
                {contentData.map((item, index) => (
                  <Box key={index}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={700}>{item.value.toLocaleString()}</Typography>
                        <Chip
                          size="small"
                          label={`+${item.today}`}
                          sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </Stack>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((item.value / Math.max(...contentData.map(d => d.value))) * 100, 100)}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: COLORS[index], borderRadius: 1 } }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Engagement Metrics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.engagementMetrics', 'Métriques d\'engagement')}
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('analytics.engagementRate', 'Taux d\'engagement')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#2563eb">{overview.engagement.engagement_rate}</Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('analytics.activeUsers7d', 'Utilisateurs actifs (7j)')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#16a34a">{overview.engagement.active_users_7d}</Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('analytics.avgPostsPerDay', 'Moy. posts/jour')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#7c3aed">{overview.engagement.avg_posts_per_day.toFixed(1)}</Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        {overview.alerts && overview.alerts.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #fef3c7', boxShadow: 'none', bgcolor: '#fffbeb' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#92400e" gutterBottom>
                  {t('analytics.alerts', 'Alertes')} ({overview.alerts_count})
                </Typography>
                <Stack spacing={1}>
                  {overview.alerts.slice(0, 5).map((alert, index) => (
                    <Alert key={index} severity={alert.type === 'error' ? 'error' : alert.type === 'warning' ? 'warning' : 'info'}>
                      <Typography variant="body2">{alert.message}</Typography>
                    </Alert>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ===== ENGAGEMENT TAB =====
interface EngagementTabProps {
  engagement: EngagementAnalyticsResponse | null;
  loading: boolean;
}

const EngagementTab: React.FC<EngagementTabProps> = ({ engagement, loading }) => {
  const { t } = useTranslation();

  if (loading || !engagement) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Format chart data
  const chartData = engagement.charts.daily_posts.map(item => ({
    date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    posts: item.count,
  }));

  // Reaction types data
  const reactionData = Object.entries(engagement.reactions.by_type).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
  }));

  // Post types data
  const postTypeData = Object.entries(engagement.posts.by_type).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
  }));

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.postsInPeriod', 'Posts (période)')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.posts.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.posts.avg_per_day.toFixed(1)}/jour
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 2 }}>
                  <ArticleIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.comments', 'Commentaires')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.comments.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.comments.avg_per_post.toFixed(1)}/post
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: 2 }}>
                  <CommentIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.reactions', 'Réactions')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.reactions.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.reactions.avg_per_post.toFixed(1)}/post
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: 2 }}>
                  <FavoriteIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.reposts', 'Reposts')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.reposts.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.reposts.avg_per_day.toFixed(1)}/jour
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: 2 }}>
                  <ShareIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Daily Posts Chart */}
        {chartData.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('analytics.dailyPostsChart', 'Posts quotidiens')}
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        labelStyle={{ fontWeight: 600, color: '#1e293b' }}
                      />
                      <Area type="monotone" dataKey="posts" name="Posts" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorPosts)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Reaction Types */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.reactionTypes', 'Types de réactions')}
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={reactionData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {reactionData.map((_, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Post Types */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.postTypes', 'Types de posts')}
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={postTypeData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={false} width={80} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                    />
                    <Bar dataKey="value" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Posts */}
        {engagement.top_posts && engagement.top_posts.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('analytics.topPosts', 'Top Posts')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('analytics.postTitle', 'Titre')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('analytics.author', 'Auteur')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('analytics.views', 'Vues')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('analytics.shares', 'Partages')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {engagement.top_posts.map((post, index) => (
                        <TableRow key={post.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                              {post.title || 'Sans titre'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{post.author?.charAt(0)}</Avatar>
                              <Typography variant="body2">{post.author}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="#2563eb">
                              {post.views?.toLocaleString() || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="#7c3aed">
                              {post.shares?.toLocaleString() || 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ===== CHURCHES TAB =====
interface ChurchesTabProps {
  churches: ChurchAnalyticsResponse | null;
  loading: boolean;
}

const ChurchesTab: React.FC<ChurchesTabProps> = ({ churches, loading }) => {
  const { t } = useTranslation();

  if (loading || !churches) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Size distribution data
  const sizeData = [
    { name: churches.size_distribution.small.label, value: churches.size_distribution.small.count, color: '#16a34a' },
    { name: churches.size_distribution.medium.label, value: churches.size_distribution.medium.count, color: '#d97706' },
    { name: churches.size_distribution.large.label, value: churches.size_distribution.large.count, color: '#dc2626' },
  ];

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('analytics.totalChurches', 'Total Églises')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                {churches.totals.total_churches}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('analytics.activeChurches', 'Actives')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#16a34a" sx={{ mt: 0.5 }}>
                {churches.totals.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('analytics.totalMemberships', 'Total Membres')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#2563eb" sx={{ mt: 0.5 }}>
                {churches.memberships.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('analytics.avgMembers', 'Moy./Église')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#7c3aed" sx={{ mt: 0.5 }}>
                {churches.memberships.avg_per_church.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Size Distribution */}
        <Grid item xs={12} md={5}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.sizeDistribution', 'Distribution par taille')}
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={sizeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {sizeData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Churches by Members */}
        <Grid item xs={12} md={7}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.topByMembers', 'Top par membres')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('analytics.churchName', 'Église')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('analytics.members', 'Membres')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('analytics.events', 'Événements')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('analytics.status', 'Statut')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {churches.top_by_members.slice(0, 5).map((church, index) => (
                      <TableRow key={church.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#7c3aed', fontSize: 14 }}>
                              <ChurchIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{church.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="#2563eb">{church.members}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{church.events}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={church.is_active ? t('analytics.active', 'Active') : t('analytics.inactive', 'Inactive')}
                            sx={{
                              bgcolor: church.is_active ? '#dcfce7' : '#fef2f2',
                              color: church.is_active ? '#16a34a' : '#dc2626',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// ===== ALARMS TAB =====
interface AlarmsTabProps {
  alarms: AlarmStatisticsResponse | null;
  loading: boolean;
}

const AlarmsTab: React.FC<AlarmsTabProps> = ({ alarms, loading }) => {
  const { t } = useTranslation();

  if (loading || !alarms) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Trend chart data
  const trendData = alarms.trend_chart.map(item => ({
    date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    alarmes: item.count,
  }));

  // Alarm types data
  const typeData = [
    { name: 'Push', value: alarms.by_type.push, color: '#2563eb' },
    { name: 'SMS', value: alarms.by_type.sms, color: '#16a34a' },
    { name: 'Email', value: alarms.by_type.email, color: '#d97706' },
    { name: 'In-App', value: alarms.by_type.in_app, color: '#7c3aed' },
  ];

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('alarms.totalAlarms', 'Total')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                {alarms.totals.total_alarms}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('alarms.sent', 'Envoyées')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#16a34a" sx={{ mt: 0.5 }}>
                {alarms.totals.sent}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('alarms.pending', 'En attente')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#d97706" sx={{ mt: 0.5 }}>
                {alarms.totals.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('alarms.missed', 'Manquées')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#dc2626" sx={{ mt: 0.5 }}>
                {alarms.totals.missed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t('alarms.successRate', 'Taux succès')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="#2563eb" sx={{ mt: 0.5 }}>
                {alarms.totals.success_rate}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Trend Chart */}
        {trendData.length > 0 && (
          <Grid item xs={12} md={8}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('alarms.trendChart', 'Tendance des alarmes')}
                </Typography>
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                      />
                      <Line type="monotone" dataKey="alarmes" stroke="#2563eb" strokeWidth={2} dot={{ fill: '#2563eb', strokeWidth: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Alarm Types */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('alarms.byType', 'Par type')}
              </Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={typeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {typeData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Events */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('alarms.topEvents', 'Top événements')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('alarms.eventTitle', 'Événement')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('alarms.date', 'Date')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('alarms.alarmCount', 'Alarmes')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alarms.events.top_events.map((event) => (
                      <TableRow key={event.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                            {event.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(event.date).toLocaleDateString('fr-FR')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="#2563eb">{event.alarm_count}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Users */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('alarms.topUsers', 'Top utilisateurs')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('userAnalytics.name', 'Nom')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('alarms.alarmCount', 'Alarmes')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alarms.users.top_users.map((user, index) => (
                      <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#7c3aed', fontSize: 12 }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="#7c3aed">{user.alarm_count}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard;
